import { addScript } from '@tunein/web-utils';
import $ from 'jquery';
import filter from 'lodash/filter';
import find from 'lodash/find';
import first from 'lodash/first';
import forEach from 'lodash/forEach';
import get from 'lodash/get';
import pull from 'lodash/pull';
import toPairs from 'lodash/toPairs';
import { waitForConsent } from '../../../../js/utils/consent';
import { MediumTypes } from '../../../../js/utils/constants';
import { v4 as uuidv4 } from 'uuid';
import logger from '../../../logging/logger';
import PausableAdTimer from '../../../utils/PausableAdTimer';
import adsSettings from '../../../utils/adsSettings';
import { State, addStateChangeListener, getState, removeStateChangeListener, updateState, } from '../../../utils/applicationState';
import configUtils from '../../../utils/configUtils';
import parseUtils from '../../../utils/parseUtils';
import targetBuilder from '../../../utils/targetBuilder';
import Events from '../../media/Events';
import mediaEvents from '../../media/mediaEvents';
import uamNetworkController from '../../networks/uamNetworkController';
import reporter from '../../reporter';
import { addSlotVisibilityChangeListener, onCanShowAdsChange, onComplete, onImpression, onResponse, onTabFocusChange, onView, setPubAdsConsentStatus, setPubAdsRestrictedDataProcessing, showOrRefreshAds, } from './displayAdHandlers';
const displayAdController = {
    slotDataArray: [],
    slotsChanged: false,
    isInitialized: false,
    async init() {
        if (this.isInitialized) {
            return;
        }
        const isInDiscordIFrame = getState(State.isInDiscordIFrame);
        googletag.cmd.push(() => {
            const partnerId = getState(State.partnerId);
            const options = getState(State.options);
            const disabledAdParamsMap = get(options, 'ads.display.params.disabled.map');
            if (!disabledAdParamsMap) {
                googletag.pubads().enableSingleRequest();
            }
            googletag.enableServices();
            googletag
                .pubads()
                .addEventListener('slotResponseReceived', onResponse.bind(this));
            googletag
                .pubads()
                .addEventListener('slotRenderEnded', onImpression.bind(this));
            googletag
                .pubads()
                .addEventListener('impressionViewable', onView.bind(this));
            googletag.pubads().setTargeting('partnerId', partnerId);
            const areSafeFramesEnabled = parseUtils.parseBool(get(options, 'ads.safeframes.enabled'));
            if (areSafeFramesEnabled) {
                googletag.pubads().setSafeFrameConfig({
                    allowOverlayExpansion: false,
                    allowPushExpansion: false,
                    sandbox: true,
                });
                googletag.pubads().setForceSafeFrame(true);
            }
            const isSlotVisibilityExperimentEnabled = parseUtils.parseBool(get(options, 'ads.pauseRefresh.enabled'));
            if (isSlotVisibilityExperimentEnabled) {
                addSlotVisibilityChangeListener(this.slotDataArray);
            }
        });
        await setPubAdsConsentStatus();
        await setPubAdsRestrictedDataProcessing();
        uamNetworkController.init(isInDiscordIFrame);
        addStateChangeListener(State.tabFocus, onTabFocusChange.bind(this));
        this.canShowAds = adsSettings.canShowAds();
        const boundOnCanShowAdsChange = onCanShowAdsChange.bind(this);
        addStateChangeListener(State.selectedGuideId, boundOnCanShowAdsChange);
        addStateChangeListener(State.playerStatus, boundOnCanShowAdsChange);
        addStateChangeListener(State.adsEnabled, boundOnCanShowAdsChange);
        const options = getState(State.options);
        const openXHeader = get(options, 'ads.openx.url');
        if (openXHeader) {
            addScript({ src: openXHeader, async: true }).catch(() => { });
        }
        this.isInitialized = true;
    },
    isRegistered(slot) {
        return Boolean(find(this.slotDataArray, ['mintSlot', slot]));
    },
    registerSlot(slot) {
        if (!slot) {
            return;
        }
        const format = first(filter(slot.formats, ['adProvider', 'dfp']));
        if (!format) {
            return;
        }
        return new Promise((resolve) => {
            googletag.cmd.push(() => {
                const dfpSlot = googletag.defineSlot(format.adUnitId, format.dimensions, slot.elementId);
                if (dfpSlot) {
                    dfpSlot.addService(googletag.pubads());
                    const slotData = {
                        dfpSlot: dfpSlot,
                        mintSlot: slot,
                        canRefresh: false,
                    };
                    const refreshRate = get(slot, 'formatDefinition.refreshRate') ||
                        configUtils.getGlobalRefreshRate();
                    slotData.timer = new PausableAdTimer(onComplete.bind(this, slotData), refreshRate * 1000);
                    logger.info({
                        message: 'displayAdController.registerSlot.debug.dimensions.issue',
                        data: {
                            slot: {
                                name: slot.name,
                                formats: slot.formats?.map((item) => ({
                                    dimensions: item.dimensions,
                                })),
                            },
                        },
                    });
                    this.slotDataArray.push(slotData);
                    this.slotsChanged = true;
                    resolve();
                }
            });
        });
    },
    deregisterSlot(slot) {
        if (slot) {
            const slotData = find(this.slotDataArray, [
                'mintSlot.elementId',
                slot.elementId,
            ]);
            if (slotData) {
                googletag.cmd.push(() => {
                    googletag.destroySlots([slotData.dfpSlot]);
                    pull(this.slotDataArray, slotData);
                    if (slotData.timer.timerId) {
                        slotData.timer.clear();
                    }
                    this.slotsChanged = true;
                });
            }
        }
    },
    serveAd(params) {
        const { slot } = params;
        return new Promise(async (resolve) => {
            const options = getState(State.options);
            const bannerAdsEnabled = parseUtils.parseBool(get(options, 'bannerads.enabled', true));
            const videoAdPlaying = getState(State.isPlayingVideoAd);
            if (!(this.isInitialized &&
                slot &&
                adsSettings.canShowAds() &&
                bannerAdsEnabled)) {
                resolve();
                return;
            }
            if (videoAdPlaying) {
                this.clearSlot(slot);
                resolve();
                return;
            }
            await waitForConsent();
            await setPubAdsConsentStatus();
            slot.requestId = uuidv4().toString();
            this.slotRefreshTriggered = false;
            googletag.cmd.push(async () => {
                const slotData = find(this.slotDataArray, [
                    'mintSlot.elementId',
                    slot.elementId,
                ]);
                if (!slotData) {
                    resolve();
                    return;
                }
                slotData.dfpSlot.clearTargeting();
                const targetingInfo = targetBuilder.build();
                const audioVideoAdId = getState(State.audioVideoAdId);
                if (audioVideoAdId) {
                    targetingInfo.audioVideoAdId = audioVideoAdId;
                    updateState(State.audioVideoAdId, undefined);
                }
                const audioVideoAdCreativeId = getState(State.audioVideoAdCreativeId);
                if (audioVideoAdCreativeId) {
                    targetingInfo.audioVideoAdCreativeId = audioVideoAdCreativeId;
                    updateState(State.audioVideoAdCreativeId, undefined);
                }
                if (!adsSettings.isUnlocked() && get(targetingInfo, 'campaignId')) {
                    delete targetingInfo.campaignId;
                }
                forEach(toPairs(targetingInfo), (pair) => {
                    slotData.dfpSlot.setTargeting(pair[0], pair[1]);
                });
                if (this.slotsChanged) {
                    this.slotsChanged = false;
                }
                const amazonBidderEnabled = !!(get(options, 'ads.amazonBidder.isEnabled', false) && apstag);
                if (amazonBidderEnabled) {
                    await uamNetworkController.fetchBids({
                        slotID: slot.elementId,
                        slotName: slot.formatDefinition.adUnitId,
                        sizes: slot.formatDefinition.dimensions,
                    });
                }
                showOrRefreshAds.bind(this)(amazonBidderEnabled, slotData, slot);
                if (slot?.formatDefinition?.reportRequest &&
                    slot.medium === MediumTypes.display) {
                    reporter.request(slot);
                    mediaEvents
                        .event(Events.Media.display, Events.Status.requested)
                        .emit({
                        slot,
                        isCompanionAd: false,
                    });
                }
                if (slotData.timer.timerId) {
                    slotData.timer.clear();
                }
                const isSlotVisibilityExperimentEnabled = parseUtils.parseBool(get(options, 'ads.pauseRefresh.enabled'));
                if (isSlotVisibilityExperimentEnabled && !slot.isAdSlotVisible()) {
                    resolve();
                    return;
                }
                slotData.timer.resume();
                resolve();
            });
        });
    },
    clearSlot(slot, shouldIgnoreAdVisibilityEventOnce) {
        if (slot) {
            const $slotEl = $(`#${slot.elementId}`);
            if ($slotEl) {
                $slotEl.empty();
                $slotEl.off();
            }
            const slotData = find(this.slotDataArray, [
                'mintSlot.elementId',
                slot.elementId,
            ]);
            if (slotData) {
                if (shouldIgnoreAdVisibilityEventOnce) {
                    slotData.shouldIgnoreAdVisibilityEventOnce =
                        shouldIgnoreAdVisibilityEventOnce;
                    return;
                }
                slotData.canRefresh = false;
            }
        }
    },
    releaseSlot(slot) {
        if (!slot) {
            return;
        }
        const releaseSlotData = find(this.slotDataArray, [
            'mintSlot.elementId',
            slot.elementId,
        ]);
        if (!releaseSlotData) {
            return;
        }
        if (releaseSlotData.timer.timerId) {
            releaseSlotData.timer.clear();
        }
        if (releaseSlotData.onTabFocus) {
            removeStateChangeListener(State.tabFocus, releaseSlotData.onTabFocus);
            releaseSlotData.onTabFocus = undefined;
        }
    },
};
export default displayAdController;
